// Core logic for mirroring the UI language onto the User row (SIS-483).
// Pure and dependency-injected so it can be unit-tested; the wiring to
// i18next / Firebase Auth / the generated SDK lives in
// src/services/languageSyncService.ts.

export const SUPPORTED_LANGUAGES = ["en", "de"] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Reduces an i18next language tag to a supported two-letter code
 * ("de-DE" → "de"). Returns null for anything outside EN/DE so callers
 * never persist values the email system can't act on.
 */
export function normalizeLanguage(lng: unknown): SupportedLanguage | null {
  if (typeof lng !== "string") return null;
  const base = lng.toLowerCase().split("-")[0];
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base)
    ? (base as SupportedLanguage)
    : null;
}

export interface LanguageSyncDeps {
  getLanguage: () => string | undefined;
  /**
   * Persists the language and resolves with the number of rows the update
   * affected. 0 means the User row does not exist yet (first signup) and
   * the write must not count as done.
   */
  pushLanguage: (language: SupportedLanguage) => Promise<number>;
  /**
   * Mirrors a confirmed in-session language switch to HubSpot (SIS-578).
   * Called only after the DB write confirmed one row, and only when the
   * language differs from the one in effect at sign-in — that one already
   * travels with the login's upsertHubspotContact call, so this never adds
   * a HubSpot request to a plain login. A rejection is reported through
   * onError and retried on the same backoff schedule as a failed push.
   */
  onLanguageChanged?: (language: SupportedLanguage) => Promise<void>;
  /** Receives push failures (for Sentry). Defaults to console.warn. */
  onError?: (err: unknown) => void;
  /** Timer injection point for tests. Defaults to setTimeout. */
  scheduleRetry?: (fn: () => void, delayMs: number) => void;
}

export interface LanguageSync {
  handleAuthChanged: (uid: string | null) => void;
  handleLanguageChanged: () => void;
  /**
   * Signal that UpsertUser completed. The upsert carries its own language
   * snapshot on every login, so a slow one can land after a newer sync
   * write; dropping the confirmed state forces a reconcile that re-asserts
   * the latest selection.
   */
  handleProfileUpserted: () => void;
}

// Backoff schedule for self-retries after a failed or zero-row write. Once
// exhausted, the sync waits for the next external trigger (auth/language/
// upsert event, or the browser coming back online).
const RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 16_000];

/**
 * Best-effort background sync: pushes the current language when a user
 * signs in and on every language switch. Writes are serialized through a
 * single reconcile loop so concurrent triggers can't land out of order,
 * and a language only counts as written once the mutation confirms it
 * touched exactly one row. Anything else (0 rows, network failure) leaves
 * the state dirty and schedules a bounded exponential retry — the column
 * feeds automatic emails, so a missed write must never surface to the user.
 */
export function createLanguageSync({
  getLanguage,
  pushLanguage,
  onLanguageChanged,
  onError = (err) =>
    console.warn("Failed to sync language to user profile:", err),
  scheduleRetry = (fn, delayMs) => {
    setTimeout(fn, delayMs);
  },
}: LanguageSyncDeps): LanguageSync {
  let uid: string | null = null;
  let written: SupportedLanguage | null = null;
  let reconciling = false;
  let retryAttempt = 0;
  let retryPending = false;

  // HubSpot mirror state, kept apart from the DB loop so a slow or hung
  // HubSpot call never delays a DB write. `mirrored` is what we assume
  // HubSpot holds for this session: seeded with the sign-in language (the
  // login upsert carries it), advanced by an acknowledged mirror, and
  // dropped to null by a failed one, since a failure does not prove the
  // remote write was unapplied.
  let mirrored: SupportedLanguage | null = null;
  let mirroring = false;
  let mirrorRetryAttempt = 0;
  let mirrorRetryPending = false;

  const scheduleRetryIfBudgeted = () => {
    if (retryPending) return;
    const delayMs = RETRY_DELAYS_MS[retryAttempt];
    if (delayMs === undefined) return; // exhausted — wait for a trigger
    retryAttempt++;
    retryPending = true;
    scheduleRetry(() => {
      retryPending = false;
      void reconcile();
    }, delayMs);
  };

  const scheduleMirrorRetryIfBudgeted = () => {
    if (mirrorRetryPending) return;
    const delayMs = RETRY_DELAYS_MS[mirrorRetryAttempt];
    if (delayMs === undefined) return; // exhausted — wait for a trigger
    mirrorRetryAttempt++;
    mirrorRetryPending = true;
    scheduleRetry(() => {
      mirrorRetryPending = false;
      void mirror();
    }, delayMs);
  };

  // Serialized HubSpot writer. Only ever mirrors what the DB has confirmed
  // (`written`), so it can never run ahead of the column that feeds
  // transactional emails, and it re-reads `written` after every call so a
  // switch made while a call was in flight is picked up next.
  const mirror = async () => {
    if (!onLanguageChanged || mirroring) return;
    mirroring = true;
    try {
      while (uid) {
        const target = written;
        if (!target || target === mirrored) return;
        const startedUid = uid;
        try {
          await onLanguageChanged(target);
        } catch (err) {
          onError(err);
          // Auth switched mid-flight: the failure belongs to the previous
          // account; reconcile the current one instead.
          if (uid !== startedUid) continue;
          mirrored = null;
          scheduleMirrorRetryIfBudgeted();
          return;
        }
        if (uid !== startedUid) continue;
        mirrored = target;
        mirrorRetryAttempt = 0;
      }
    } finally {
      mirroring = false;
    }
  };

  const reconcile = async () => {
    if (reconciling) return;
    reconciling = true;
    try {
      while (uid) {
        const target = normalizeLanguage(getLanguage());
        if (!target || target === written) return;
        const startedUid = uid;
        let rows: number;
        try {
          rows = await pushLanguage(target);
        } catch (err) {
          onError(err);
          scheduleRetryIfBudgeted();
          return;
        }
        // Auth switched mid-flight: the write went out under the previous
        // account's token. Loop again to reconcile the current account.
        if (uid !== startedUid) continue;
        if (rows !== 1) {
          // Row missing (first signup) — retry until it exists.
          scheduleRetryIfBudgeted();
          return;
        }
        written = target;
        retryAttempt = 0;
        // Hand the confirmed value to HubSpot without waiting on it.
        void mirror();
        // Loop once more: the language may have changed while in flight.
      }
    } finally {
      reconciling = false;
    }
  };

  // Every external trigger is a fresh reason to sync, so it restores the
  // full retry budgets.
  const trigger = () => {
    retryAttempt = 0;
    mirrorRetryAttempt = 0;
    void reconcile();
    void mirror();
  };

  return {
    handleAuthChanged(nextUid: string | null) {
      uid = nextUid;
      // A different account (or a fresh login) must get its own write —
      // the confirmed value belongs to the previous session.
      written = null;
      // The language in effect at sign-in reaches HubSpot through the
      // login upsert; only a later switch needs the mirror.
      mirrored = normalizeLanguage(getLanguage());
      trigger();
    },
    handleLanguageChanged() {
      trigger();
    },
    handleProfileUpserted() {
      written = null;
      trigger();
    },
  };
}
